<template>
  <div class="post-editor">
    <dropdown-select v-model="postStatus" label="Publication Status" :options="postStatusList" class="mr-base" />
    <attachments-editor ref="attachments-editor" v-model="attachments" :author-id="authorId" :poster="posterId"
      @remove="onRemoveAttachment" @set-poster="posterId = $event" />
    <text-editor ref="text-editor" v-model="content" label="CV" />
  </div>
</template>

<script>
import { db } from '@/lib/firebase'
import tagsLib from '@/lib/tags'
import PostEditorMixin from '@/mixins/post-editor'
import TextEditor from '../../components/UI/TextEditor'
import DropdownSelect from '../../components/UI/DropdownSelect'
import AttachmentsEditor from '../attachments/AttachmentsEditor'

export default {
  name: 'CVEditor',
  mixins: [PostEditorMixin],
  components: { DropdownSelect, TextEditor, AttachmentsEditor },
  props: {
    open: { type: Boolean, required: true },
    value: { type: [Object, String], default: null }
  },

  data: () => ({
    postData: {},
    emptyPostData: {
      id: 'cv',
      author: null,
      title: 'CV',
      attachments: null,
      thumbnail: '',
      content: '',
      excerpt: '',
      status: 'public'
    },
    posterTempId: '',
    textEditorFocused: false,
    unsubscribe: null,
    institutions: null,
    contentTemplates: {
      '--': 'Default',
      Residency: 'AIR',
      Partners: 'Partners',
      About: 'About'
    }
  }),

  computed: {
    people: {
      get () { return this.postData.relatedPeople || [] },
      set (newValue) { this.$set(this.postData, 'relatedPeople', newValue) }
    },
    template: {
      get () { return this.postData.template || '--' },
      set (newValue) { this.$set(this.postData, 'template', newValue) }
    },
    allowDelete () {
      return this.adminOrEditor && (this.value || {}).id
    }
  },

  created () {
    this.collectionRef = db.collection('pages')
    this.setPostData()
  },

  mounted () {
    this.$emit('allowDelete', this.allowDelete)
  },

  methods: {
    isProperCVData (val) {
      return !!val && typeof val === 'object' && val.id === 'cv'
    },

    setPostData () {
      if (this.isProperCVData(this.value)) {
        this.postData = JSON.parse(JSON.stringify(this.value))
        return
      }

      const loadCvDoc = () => this.collectionRef.doc('cv')
        .get()
        .then(doc => {
          if (doc.exists) {
            this.postData = { id: 'cv', ...doc.data() }
          } else {
            this.postData = { ...this.defaultPostData }
          }
        })
        .catch(() => {
          this.postData = { ...this.defaultPostData }
        })

      if (typeof this.value === 'string') {
        loadCvDoc()
      } else {
        loadCvDoc()
      }
    },

    async save () {
      if (!this.postData.author) this.postData.author = this.author
      if (!this.postData.author) throw new Error('Cant save post without author!')
      try {
        this.$emit('setProcessing', true)
        const attachmentsEditor = this.$refs['attachments-editor']
        if (attachmentsEditor) {
          const attachmentsData = await attachmentsEditor.processAttachments()
          this.$set(this.postData, 'attachments', attachmentsData)
          if (this.posterTempId) {
            this.$set(this.postData, 'thumbnail', this.posterTempId)
          }
        }

        if ((this.postData.tags || []).length) {
          await Promise.all(this.postData.tags.filter(tag => tag.new).map(tag => {
            return tagsLib.saveTag(tag)
          }))
          this.postData.tags = this.postData.tags.map(tag => ({ id: tag.id, title: tag.title }))
        }

        if (!this.postData.date) {
          this.postData.date = new Date().getTime()
        }

        // Always save CV to a unique, stable document id 'cv'
        await this.collectionRef.doc('cv').set(this.postData, { merge: true })
        this.$emit('setProcessing', false)
        this.$emit('saved')
        this.$emit('close')
      } catch (e) {
        this.$emit('setProcessing', false)
        console.error('%c savePost() %c e: ', 'background:#ff00AA;color:#000', 'color:#00aaff', e)
      }
    },
    async afterDeleteFunc () {
    }
  }
}
</script>
